import { App, Button, Form, Input, Modal, Row, Col, Select, Space, Switch } from "antd";
import { CloseSquareFilled, MinusCircleOutlined, PlusOutlined, SaveFilled } from '@ant-design/icons';
import { useEffect, useMemo } from "react";
import execute from "hooks/execute";
import { protectedResources } from "config/msalConfig";

const ALL_MODULES_VALUE = "__ALL__";

const normalizeRoleName = (name) => (name || '').trim().toLowerCase();

export const DataEditor = ({
    isOpen,
    dataItem,
    onDataEditorFinished,
    onDataEditorCancelled,
    availableModules = [],
    existingRoleNames = []
}) => {
    const [form] = Form.useForm();
    const { message } = App.useApp();

    const moduleOptions = useMemo(() => {
        const roleModuleOptions = (availableModules || []).map((module) => ({
            value: module.shortName || module.value,
            label: module.label || `${module.moduleName || module.ModuleName} (${module.shortName || module.ShortName || module.value})`
        }));

        return [{ value: ALL_MODULES_VALUE, label: 'All' }, ...roleModuleOptions];
    }, [availableModules]);

    const allModuleShortNames = useMemo(
        () => moduleOptions.filter((option) => option.value !== ALL_MODULES_VALUE).map((option) => option.value),
        [moduleOptions]
    );

    const existingRoleNameSet = useMemo(
        () => new Set((existingRoleNames || []).map((name) => normalizeRoleName(name))),
        [existingRoleNames]
    );

    const handleDataEditFinished = async (values) => {
        const roles = (values.roles || []).map((role) => ({
            name: (role.role_name || '').trim(),
            status: role.status ? 1 : 0,
            modules: Array.from(new Set((role.modules || []).filter(Boolean)))
        }));

        try {
            const response = await execute('POST', `${protectedResources.api.baseEndPoint}/customerRoles/bulk`, {
                customerId: Number(dataItem.customerId),
                organizationId: Number(dataItem.organizationId),
                roles
            });

            if (response?.success) {
                message.success(`${roles.length} role(s) added successfully`);
                onDataEditorFinished(response.data);
            } else {
                message.error(response?.message || 'Failed to add customer roles');
            }
        } catch (error) {
            message.error(error?.response?.data?.message || error.message || 'Failed to add customer roles');
        }
    };

    const handleDataEditCancelled = () => {
        onDataEditorCancelled();
    };

    const handleModulesChange = (fieldIndex, selectedValues) => {
        const selected = selectedValues || [];
        if (selected.includes(ALL_MODULES_VALUE)) {
            form.setFieldValue(['roles', fieldIndex, 'modules'], allModuleShortNames);
            return;
        }

        form.setFieldValue(
            ['roles', fieldIndex, 'modules'],
            selected.filter((value) => value !== ALL_MODULES_VALUE)
        );
    };

    const validateRoleName = (fieldIndex) => async (_, value) => {
        const normalized = normalizeRoleName(value);
        if (!normalized) {
            return Promise.reject(new Error('Role Name is required'));
        }

        if (existingRoleNameSet.has(normalized)) {
            return Promise.reject(new Error('Role name already exists for this customer'));
        }

        const roles = form.getFieldValue('roles') || [];
        const duplicateInForm = roles.some((role, idx) => (
            idx !== fieldIndex && normalizeRoleName(role?.role_name) === normalized
        ));

        if (duplicateInForm) {
            return Promise.reject(new Error('Role name is duplicated in this request'));
        }

        return Promise.resolve();
    };

    useEffect(() => {
        if (isOpen) {
            form.setFieldsValue({
                roles: [{ role_name: '', status: true, modules: [] }]
            });
        }
    }, [isOpen, form]);

    return (
        <Modal
            open={isOpen}
            title="Add Customer Role"
            width={980}
            okText={<Space><SaveFilled />Save</Space>}
            cancelText={<Space><CloseSquareFilled />Cancel</Space>}
            okButtonProps={{ autoFocus: true, htmlType: 'submit' }}
            onCancel={handleDataEditCancelled}
            destroyOnClose
            modalRender={(dom) => (
                <Form
                    layout="vertical"
                    form={form}
                    name="dataEditorForm"
                    clearOnDestroy
                    onFinish={handleDataEditFinished}
                >
                    {dom}
                </Form>
            )}
        >
            <Form.List name="roles">
                {(fields, { add, remove }) => (
                    <>
                        {fields.map((field, index) => (
                            <Row key={field.key} gutter={12} align="top" style={{ marginBottom: 8 }}>
                                <Col span={10}>
                                    <Form.Item
                                        label={index === 0 ? 'Role Name' : ''}
                                        name={[field.name, 'role_name']}
                                        validateTrigger={['onChange', 'onBlur']}
                                        rules={[{ validator: validateRoleName(field.name) }]}
                                    >
                                        <Input placeholder="Enter role name" />
                                    </Form.Item>
                                </Col>

                                <Col span={4}>
                                    <Form.Item
                                        label={index === 0 ? 'Status' : ''}
                                        name={[field.name, 'status']}
                                        valuePropName="checked"
                                    >
                                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" defaultChecked />
                                    </Form.Item>
                                </Col>

                                <Col span={8}>
                                    <Form.Item
                                        label={index === 0 ? 'Modules' : ''}
                                        name={[field.name, 'modules']}
                                        rules={[{ required: true, message: 'Select at least one module' }]}
                                    >
                                        <Select
                                            mode="multiple"
                                            allowClear
                                            placeholder="Select modules"
                                            options={moduleOptions}
                                            onChange={(values) => handleModulesChange(field.name, values)}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col span={2}>
                                    {fields.length > 1 ? (
                                        <Button
                                            type="text"
                                            danger
                                            icon={<MinusCircleOutlined />}
                                            style={{ marginTop: index === 0 ? 30 : 0 }}
                                            onClick={() => remove(field.name)}
                                        />
                                    ) : null}
                                </Col>
                            </Row>
                        ))}

                        <Form.Item style={{ marginTop: 4 }}>
                            <Button
                                type="dashed"
                                onClick={() => add({ role_name: '', status: true, modules: [] })}
                                icon={<PlusOutlined />}
                                block
                            >
                                Add Role
                            </Button>
                        </Form.Item>
                    </>
                )}
            </Form.List>
        </Modal>
    );
};
